import { useCallback, useRef } from 'react';
import { updateStringsItem, updateTranslationLine, createTranslationLine } from '@/lib/stores/stringsStore';
import { useToast } from '@/hooks/use-toast';
import { getSelectedGame } from '@/lib/stores/unifiedUserStore';

export const useTMSStringUpdates = () => {
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const updateStringField = useCallback(async (
    row: any,
    columnKey: string,
    newValue: string
  ) => {
    console.log('🔥 useTMSStringUpdates: updateStringField called', {
      rowId: row?.id,
      columnKey,
      newValue,
      newValueLength: newValue?.length || 0,
      isEmptyOrWhitespace: !newValue || newValue.trim() === '',
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(1, 5)
    });
    
    try {
      // Get the original item from the row data
      const originalItem = row;
      if (!originalItem) {  
        throw new Error('Original item not found in row data');
      }


      // Check if this is a translation update (nested field path)
      if (columnKey.includes('.')) {
        // Handle translation updates
        const pathParts = columnKey.split('.');
        if (pathParts[0] === 'translations' && pathParts.length === 3) {
          const [, languageCode, fieldKey] = pathParts;
          
          // Find the existing translation or create a new one
          const existingTranslation = originalItem.originalTranslations?.find(
            (t: any) => t.language_code === languageCode
          );

          console.log('🔍 useTMSStringUpdates: Translation lookup', {
            languageCode,
            fieldKey,
            newValue,
            existingTranslation: existingTranslation ? { id: existingTranslation.id, text: existingTranslation.translated_text } : null,
            originalTranslationsCount: originalItem.originalTranslations?.length || 0
          });

          if (existingTranslation) {
            // Update existing translation
            // Map display field name to API field name
            const apiFieldKey = fieldKey === 'text' ? 'translated_text' : fieldKey;
            
            const updatedTranslation = {
              ...existingTranslation,
              [apiFieldKey]: apiFieldKey === 'approved' ? newValue === 'true' : newValue,
            };

            console.log('🔄 useTMSStringUpdates: Updating existing translation', {
              existingTranslation,
              updatedTranslation,
              fieldKey,
              apiFieldKey,
              newValue,
              timestamp: new Date().toISOString()
            });

            // Update the translation via API
            await updateTranslationLine(existingTranslation.id, updatedTranslation, toastRef.current);
          } else {
            // Only create new translation if there's actual content
            // Don't create empty translations just from clicking
            // Map display field name to API field name for the check
            const apiFieldKey = fieldKey === 'text' ? 'translated_text' : fieldKey;
            
            if (apiFieldKey === 'translated_text' && (!newValue || newValue.trim() === '')) {
              console.log('🚫 useTMSStringUpdates: Preventing empty translation creation', {
                fieldKey,
                apiFieldKey,
                newValue,
                languageCode,
                dialogueLineId: originalItem.id
              });
              // Don't create empty translations - just return success
              return true;
            }

            // Create new translation with the provided content
            const newTranslation = {
              dialogue_line_id: originalItem.id,
              language_code: languageCode,
              translated_text: apiFieldKey === 'translated_text' ? newValue : '',
              approved: apiFieldKey === 'approved' ? newValue === 'true' : false,
            };

            console.log('✅ useTMSStringUpdates: Creating new translation', {
              newTranslation,
              fieldKey,
              apiFieldKey,
              newValue,
              timestamp: new Date().toISOString()
            });

            await createTranslationLine(newTranslation, toastRef.current);
          }

          // Success toast removed to prevent page re-renders that cause editor focus loss
          // The user gets immediate visual feedback from the updated cell content

          return true;
        }
      }

      // Handle main table field updates
      const fieldMapping: Record<string, string> = {
        'source_text': 'source_text',
        'sourceText': 'source_text', // Map sourceText to source_text for TMSTableWidget
        'abbrev': 'abbrev',
        'section': 'section',
        'context': 'context',
        'status': 'status',
      };

      const apiField = fieldMapping[columnKey];
      if (!apiField) {
        throw new Error(`Field ${columnKey} is not editable`);
      }

      // Get the selected game and validate it exists
      const selectedGame = getSelectedGame();
      if (!selectedGame || !selectedGame.id) {
        throw new Error('No game is currently selected. Please select a game first.');
      }

      // Prepare the update data
      const updateData = {
        id: originalItem.id,
        game_id: selectedGame.id,
        [apiField]: newValue,
      };


      // Update via API and store
      await updateStringsItem(updateData, toastRef.current);


      // Success toast removed to prevent page re-renders that cause editor focus loss
      // The user gets immediate visual feedback from the updated cell content

      return true;
    } catch (error) {
      console.error('❌ Error updating string field:', error);
      toastRef.current({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update field',
        variant: 'destructive',
      });
      return false;
    }
  }, []);

  return { updateStringField };
}; 